/**
 * Import d'un jeu de mesures externe.
 *
 * Le fichier reste local au navigateur : il est lu comme texte puis passe par
 * le même parseur sévère que les fixtures et les tests. La vue ne peut ni
 * déclarer un dataset externe par convenance, ni ignorer une provenance
 * incomplète. Elle affiche au contraire tout ce que le parseur a retenu afin
 * que la qualité d'arbitre des mesures soit vérifiable par le lecteur.
 */

import { DATASET_LIMITS } from "../data/limits.js";
import type { Dataset, Problem } from "../core/dataset.js";
import { parseDataset } from "../core/dataset.js";
import { fr } from "../i18n/fr.js";
import { escape } from "./format.js";

export type DatasetInputState =
  | { readonly kind: "empty" }
  | {
      readonly kind: "invalid";
      readonly fileName: string;
      readonly problems: readonly Problem[];
    }
  | {
      readonly kind: "loaded";
      readonly fileName: string;
      readonly dataset: Dataset;
    };

export const EMPTY_DATASET_INPUT: DatasetInputState = { kind: "empty" };

export function datasetFromInput(state: DatasetInputState): Dataset | undefined {
  return state.kind === "loaded" ? state.dataset : undefined;
}

function problemText(problem: Problem): string {
  return problem.line > 0
    ? `${fr.datasetLine(problem.line)} — ${problem.message}`
    : problem.message;
}

function status(state: DatasetInputState): string {
  if (state.kind === "empty") {
    return [
      `<div class="verlet-dataset__status" id="verlet-dataset-status"`,
      ` data-dataset-status role="status" tabindex="-1">`,
      `<p>${escape(fr.datasetEmpty)}</p>`,
      `</div>`,
    ].join("");
  }

  if (state.kind === "invalid") {
    const problems = state.problems
      .map((problem) => `<li>${escape(problemText(problem))}</li>`)
      .join("");
    return [
      `<div class="verlet-dataset__status verlet-dataset__status--invalid"`,
      ` id="verlet-dataset-status" data-dataset-status role="alert" tabindex="-1">`,
      `<p><strong>${escape(fr.datasetRejected(state.fileName))}</strong></p>`,
      `<ul>${problems}</ul>`,
      `</div>`,
    ].join("");
  }

  const { dataset } = state;
  const facts: ReadonlyArray<readonly [string, string]> = [
    [fr.datasetName, dataset.name],
    [fr.datasetSource, dataset.source],
    [fr.datasetMethod, dataset.acquisitionMethod],
    [fr.datasetPublishedOn, dataset.publishedOn],
    [fr.datasetLicense, dataset.license],
    [fr.datasetObservationCount, fr.datasetRows(dataset.rows.length)],
  ];
  const provenance = facts
    .map(([label, value]) => `<div><dt>${escape(label)}</dt><dd>${escape(value)}</dd></div>`)
    .join("");

  return [
    `<div class="verlet-dataset__status verlet-dataset__status--loaded"`,
    ` id="verlet-dataset-status" data-dataset-status role="status" tabindex="-1">`,
    `<p><strong>${escape(fr.datasetLoaded(state.fileName))}</strong></p>`,
    `<dl class="verlet-dataset__provenance">${provenance}</dl>`,
    `<p class="verlet-dataset__authenticity">${escape(fr.datasetAuthenticityCaveat)}</p>`,
    `</div>`,
  ].join("");
}

export function renderDatasetInput(state: DatasetInputState): string {
  const clear =
    state.kind === "empty"
      ? ""
      : `<button type="button" class="verlet-chip" data-dataset-clear>${escape(fr.datasetClear)}</button>`;

  return [
    `<div class="verlet-dataset">`,
    `<p class="verlet-dataset__intro" id="verlet-dataset-help">${escape(fr.datasetHelp)}</p>`,
    `<div class="verlet-dataset__actions">`,
    `<label for="verlet-dataset-file">${escape(fr.datasetChoose)}</label>`,
    `<input type="file" id="verlet-dataset-file" data-dataset-file`,
    ` accept=".csv,text/csv" aria-describedby="verlet-dataset-help verlet-dataset-status" />`,
    `<a class="verlet-chip" href="./modele-mesures-gnomon.csv" download>${escape(fr.datasetTemplate)}</a>`,
    clear,
    `</div>`,
    status(state),
    `</div>`,
  ].join("");
}

export function bindDatasetInput(
  root: HTMLElement,
  onChange: (state: DatasetInputState) => void,
): () => void {
  let detached = false;
  let request = 0;

  const focusStatus = (): void => {
    root.querySelector<HTMLElement>("[data-dataset-status]")?.focus();
  };

  const onInput = (event: Event): void => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches("[data-dataset-file]")) return;
    const file = input.files?.[0];
    if (file === undefined) return;

    const currentRequest = request + 1;
    request = currentRequest;
    input.setAttribute("aria-busy", "true");

    if (file.size > DATASET_LIMITS.bytes) {
      input.removeAttribute("aria-busy");
      onChange({ kind: "invalid", fileName: file.name, problems: [{ line: 0, message: fr.datasetTooLarge }] });
      focusStatus();
      return;
    }
    void file
      .text()
      .then((text) => {
        if (detached || request !== currentRequest) return;
        input.removeAttribute("aria-busy");
        const loaded = parseDataset(text);
        onChange(
          loaded.ok
            ? { kind: "loaded", fileName: file.name, dataset: loaded.dataset }
            : { kind: "invalid", fileName: file.name, problems: loaded.problems },
        );
        focusStatus();
      }, () => {
        if (detached || request !== currentRequest) return;
        input.removeAttribute("aria-busy");
        onChange({
          kind: "invalid",
          fileName: file.name,
          problems: [{ line: 0, message: fr.datasetUnreadable }],
        });
        focusStatus();
      }).catch((error: unknown) => {
        if (detached || request !== currentRequest) return;
        input.removeAttribute("aria-busy");
        console.error(error);
        onChange({ kind: "invalid", fileName: file.name, problems: [{ line: 0, message: fr.datasetProcessingFailed }] });
        focusStatus();
      });
  };

  const onClick = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest("[data-dataset-clear]") === null) return;
    request += 1;
    onChange(EMPTY_DATASET_INPUT);
    root.querySelector<HTMLInputElement>("[data-dataset-file]")?.focus();
  };

  root.addEventListener("change", onInput);
  root.addEventListener("click", onClick);

  return () => {
    detached = true;
    request += 1;
    root.removeEventListener("change", onInput);
    root.removeEventListener("click", onClick);
  };
}
